import React, { useState } from "react";
import { alpha } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import moment from "moment";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import type { IScanVisitor } from "../../type/offlineExhibitorType";

interface Props {
	visitors?: IScanVisitor[];
	exhibitorName?: string;
}

const ExportScannedVisitorsExcel: React.FC<Props> = ({
	visitors,
	exhibitorName,
}) => {
	const [isExporting, setIsExporting] = useState<boolean>(false);

	const handleExportExcel = async () => {
		if (!visitors || visitors.length === 0) return;

		try {
			setIsExporting(true);
			const workbook = new Excel.Workbook();
			const worksheet = workbook.addWorksheet("Scanned Visitors");

			// Define columns
			worksheet.columns = [
				{ header: "SL", key: "sl", width: 8 },
				{ header: "Visitor ID / No", key: "visitor_no", width: 18 },
				{ header: "Visitor Name", key: "visitor_name", width: 26 },
				{ header: "Visitor Email", key: "visitor_email", width: 30 },
				{ header: "Visitor Phone", key: "visitor_phone", width: 20 },
				{ header: "Stall No", key: "stall_no", width: 16 },
				{ header: "Hall Name", key: "hall_name", width: 22 },
				{ header: "Scan Time", key: "scan_time", width: 26 },
			];

			// Style header row
			const headerRow = worksheet.getRow(1);
			headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
			headerRow.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FF2E7D32" }, // Forest Green theme
			};
			headerRow.alignment = { vertical: "middle", horizontal: "center" };
			headerRow.height = 28;

			// Add data rows
			visitors.forEach((visitor, index) => {
				const row = worksheet.addRow({
					sl: index + 1,
					visitor_no: visitor.visitor_no || "N/A",
					visitor_name: visitor.visitor_name || "N/A",
					visitor_email: visitor.visitor_email || "-",
					visitor_phone: visitor.visitor_phone || "-",
					stall_no: visitor.stall_no || "N/A",
					hall_name: visitor.hall_name || "-",
					scan_time: visitor.scan_time
						? moment(visitor.scan_time).format("DD MMM YYYY, hh:mm A")
						: "-",
				});

				row.alignment = { vertical: "middle" };
				row.height = 22;

				// Center align key identifier cells
				row.getCell("sl").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("visitor_no").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("stall_no").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("hall_name").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
			});

			// Add cell borders
			worksheet.eachRow((row) => {
				row.eachCell((cell) => {
					cell.border = {
						top: { style: "thin", color: { argb: "FFE0E0E0" } },
						left: { style: "thin", color: { argb: "FFE0E0E0" } },
						bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
						right: { style: "thin", color: { argb: "FFE0E0E0" } },
					};
				});
			});

			const buffer = await workbook.xlsx.writeBuffer();
			const safeName = exhibitorName
				? `${exhibitorName.replace(/[^a-zA-Z0-9-_]/g, "_")}_Scanned_Visitors`
				: "Exhibitor_Scanned_Visitors";
			saveAs(
				new Blob([buffer]),
				`${safeName}_${moment().format("YYYY-MM-DD")}.xlsx`
			);
		} catch (error) {
			console.error("Failed to export Excel:", error);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<LoadingButton
			variant="contained"
			color="primary"
			size="small"
			loading={isExporting}
			disabled={!visitors || visitors.length === 0}
			startIcon={<DownloadTwoToneIcon />}
			onClick={handleExportExcel}
			sx={{
				borderRadius: 1.5,
				textTransform: "none",
				fontWeight: 700,
				fontSize: "0.8125rem",
				px: 2,
				py: 0.6,
				boxShadow: (theme) =>
					`0 2px 6px ${alpha(theme.palette.primary.main, 0.25)}`,
			}}
		>
			Export Excel
		</LoadingButton>
	);
};

export default ExportScannedVisitorsExcel;
